/**
 * MOTORMIA Vehicle Picker  v1.0
 *
 * Drives the multi-step vehicle identification flow using the identifyVehicle
 * GraphQL API.  Stores the resolved vehicle in localStorage under MM_VEHICLE_KEY.
 *
 * Public API (attached to window):
 *   window.MMGarage            — get / set / clear the stored vehicle
 *   window.MMVehiclePicker     — inline picker class
 *   window.MMVehiclePickerModal — modal wrapper
 *
 * Custom events dispatched on `document`:
 *   'mm:vehicle:selected'  — detail: { vehicle }
 *   'mm:vehicle:cleared'   — no detail
 */

(function (window) {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
   * Constants
   * ───────────────────────────────────────────────────────────────────────── */

  var MM_VEHICLE_KEY = 'mm_vehicle';

  // Shopify App Proxy — no Authorization header required, Shopify signs automatically.
  // For local dev outside Shopify: set window.MM_API_URL to
  //   'https://motormia-platform-prod.fly.dev/graphql'
  // and window.MM_API_KEY to the WEAK_PUBLIC_API_KEY value.
  var MM_API_URL = window.MM_API_URL || '/apps/motormia';
  var MM_API_KEY = window.MM_API_KEY || null;

  // SVG line-art icons — Redline stroke, centered in card, matching app aesthetic
  var VEHICLE_ICONS = {
    // Sedan side profile: clean body outline + two wheels
    CAR: '<svg viewBox="0 0 56 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="40" height="23">'
       + '<path d="M4 22h48"/>'
       + '<path d="M4 22l4-8h8l4-6h16l4 6h8l4 8"/>'
       + '<circle cx="16" cy="26" r="4"/>'
       + '<circle cx="40" cy="26" r="4"/>'
       + '</svg>',

    // Pickup truck: cab + open flat bed
    TRUCK: '<svg viewBox="0 0 64 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="44" height="22">'
         + '<path d="M2 22h60"/>'
         + '<path d="M4 22l3-9h10l3-7h14l3 7v9"/>'
         + '<path d="M37 22V13h18l4 9"/>'
         + '<line x1="37" y1="13" x2="37" y2="22"/>'
         + '<circle cx="14" cy="26" r="4"/>'
         + '<circle cx="48" cy="26" r="4"/>'
         + '</svg>',

    // Motorcycle: two wheels + low frame + tank/seat hump + front fork & handlebar
    BIKE: '<svg viewBox="0 0 56 36" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="38" height="25">'
        + '<circle cx="12" cy="26" r="6"/>'
        + '<circle cx="44" cy="26" r="6"/>'
        + '<path d="M12 26 L20 26 L26 16 L36 16 L42 26"/>'
        + '<path d="M26 16 L28 12 L36 12 L36 16"/>'
        + '<path d="M42 16 L46 10 L50 10"/>'
        + '</svg>',

    // Mountains: two overlapping peaks (matches app UTV icon)
    UTV: '<svg viewBox="0 0 56 36" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="38" height="25">'
       + '<path d="M2 30 L18 8 L34 30"/>'
       + '<path d="M24 30 L38 10 L52 30"/>'
       + '</svg>',
  };

  var VEHICLE_TYPES = [
    { value: 'CAR',   label: 'Car'     },
    { value: 'TRUCK', label: 'Truck'   },
    { value: 'BIKE',  label: 'Bike'    },
    { value: 'UTV',   label: 'UTV'     },
  ];

  var YEAR_MAX = new Date().getFullYear() + 1;
  var YEAR_MIN = 1970;

  // Maps the API's `propertyName` → the IdentifyVehicleInput field we set next call.
  // NOTE: API returns "engine_name" but the input field is `engine` (documented quirk).
  var PROPERTY_TO_INPUT = {
    'make':              'makeId',           // uses option.id
    'model':             'modelId',          // uses option.id
    'submodel':          'submodel',         // uses option.value
    'drive_type':        'driveType',        // uses option.value
    'transmission_type': 'transmissionType', // uses option.value
    'engine_name':       'engine',           // uses option.value  ← quirk
    'body_or_cab_style': 'bodyOrCabStyle',   // uses option.value
  };

  // These two properties use option.id; everything else uses option.value.
  var USE_ID_FOR = { make: true, model: true };

  var PROPERTY_LABELS = {
    'make':              'Make',
    'model':             'Model',
    'submodel':          'Trim / Submodel',
    'drive_type':        'Drive Type',
    'transmission_type': 'Transmission',
    'engine_name':       'Engine',
    'body_or_cab_style': 'Body / Cab Style',
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * GraphQL query
   * ───────────────────────────────────────────────────────────────────────── */

  var IDENTIFY_VEHICLE_QUERY = [
    'query IdentifyVehicle($input: IdentifyVehicleInput!) {',
    '  identifyVehicle(input: $input) {',
    '    vehicleId',
    '    matched {',
    '      muvId makeId modelId submodelId generationId',
    '      make model generation submodel subgeneration',
    '      startYear endYear',
    '      driveType transmissionType transmissionGears',
    '      engineName engineCode engine',
    '      bodyOrCabStyle category subcategory',
    '      enthusiastLabels',
    '    }',
    '    propertyName',
    '    propertyOptions { id value }',
    '  }',
    '}',
  ].join('\n');

  /* ─────────────────────────────────────────────────────────────────────────
   * MMGarage — localStorage helper
   * ───────────────────────────────────────────────────────────────────────── */

  var MMGarage = {
    get: function () {
      try {
        var raw = localStorage.getItem(MM_VEHICLE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },

    set: function (vehicle) {
      try {
        localStorage.setItem(MM_VEHICLE_KEY, JSON.stringify(vehicle));
        document.dispatchEvent(
          new CustomEvent('mm:vehicle:selected', { detail: { vehicle: vehicle } })
        );
      } catch (e) { /* storage unavailable — silent fail */ }
    },

    clear: function () {
      localStorage.removeItem(MM_VEHICLE_KEY);
      document.dispatchEvent(new CustomEvent('mm:vehicle:cleared'));
    },

    label: function () {
      var v = this.get();
      if (!v) return null;
      return [v.year, v.make, v.model, v.submodel].filter(Boolean).join(' ');
    },
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * API helper
   * ───────────────────────────────────────────────────────────────────────── */

  function callIdentifyVehicle(input) {
    var headers = { 'Content-Type': 'application/json' };
    if (MM_API_KEY) headers['x-platform-api-key'] = MM_API_KEY;

    console.log('[MMPicker] Calling identifyVehicle with:', JSON.stringify(input));

    return fetch(MM_API_URL, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        operationName: 'IdentifyVehicle',
        query:         IDENTIFY_VEHICLE_QUERY,
        variables:     { input: input },
      }),
    })
      .then(function (res) {
        console.log('[MMPicker] HTTP status:', res.status);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        console.log('[MMPicker] Response:', JSON.stringify(json).slice(0, 500));
        if (json.errors && json.errors.length) throw new Error(json.errors[0].message);
        if (!json.data || !json.data.identifyVehicle) throw new Error('No identifyVehicle in response');
        return json.data.identifyVehicle;
      });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Utility — create element with className + optional innerHTML
   * ───────────────────────────────────────────────────────────────────────── */

  function el(tag, cls, html) {
    var node = document.createElement(tag);
    if (cls)  node.className = cls;
    if (html) node.innerHTML = html;
    return node;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * MMVehiclePicker — inline widget
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * @param {object} options
   * @param {HTMLElement} options.container   — DOM node to render into
   * @param {function}    options.onSelect    — called with vehicle object on match confirm
   * @param {function}    [options.onCancel]  — called if user cancels (modal use)
   * @param {boolean}     [options.showCancel=true]
   */
  function MMVehiclePicker(options) {
    this.container  = options.container  || null;
    this.onSelect   = options.onSelect   || function () {};
    this.onCancel   = options.onCancel   || function () {};
    this.showCancel = options.showCancel !== false;

    this._resetState();
    this._render();
  }

  MMVehiclePicker.prototype._resetState = function () {
    this.state = {
      step:            'type',  // type | year | loading | options | matched | error
      vehicleId:       Math.floor(Math.random() * 1000000) + 1, // 32-bit safe per API spec
      category:        null,
      year:            null,
      makeId:          null,
      modelId:         null,
      submodel:        null,
      driveType:       null,
      transmissionType:null,
      engine:          null,
      bodyOrCabStyle:  null,
      // API response
      propertyName:    null,
      propertyOptions: [],
      matched:         null,
      error:           null,
      // Display labels accumulated during flow
      typeLabel:       null,
      makeLabel:       null,
      modelLabel:      null,
    };
  };

  MMVehiclePicker.prototype._set = function (updates) {
    Object.assign(this.state, updates);
    this._render();
  };

  /* ── Step handlers ──────────────────────────────────────────────────────── */

  MMVehiclePicker.prototype._pickType = function (type) {
    this._set({ category: type.value, typeLabel: type.label, step: 'year' });
  };

  MMVehiclePicker.prototype._pickYear = function (year) {
    this._set({ year: parseInt(year, 10), step: 'loading' });
    this._query();
  };

  MMVehiclePicker.prototype._pickOption = function (option) {
    var propName = this.state.propertyName;
    var inputKey = PROPERTY_TO_INPUT[propName];
    if (!inputKey) return;

    var value   = USE_ID_FOR[propName] ? option.id : option.value;
    var updates = { step: 'loading' };
    updates[inputKey] = value;
    if (propName === 'make')  updates.makeLabel  = option.value;
    if (propName === 'model') updates.modelLabel = option.value;

    this._set(updates);
    this._query();
  };

  MMVehiclePicker.prototype._query = function () {
    var self  = this;
    var s     = this.state;

    var input = { vehicleId: s.vehicleId, category: s.category, year: s.year };
    if (s.makeId)            input.makeId            = s.makeId;
    if (s.modelId)           input.modelId           = s.modelId;
    if (s.submodel)          input.submodel          = s.submodel;
    if (s.driveType)         input.driveType         = s.driveType;
    if (s.transmissionType)  input.transmissionType  = s.transmissionType;
    if (s.engine)            input.engine            = s.engine;
    if (s.bodyOrCabStyle)    input.bodyOrCabStyle    = s.bodyOrCabStyle;

    callIdentifyVehicle(input)
      .then(function (result) {
        if (result.matched) {
          self.state.matched = result.matched;
          self._confirm();
          return;
        } else if (result.propertyName && result.propertyOptions && result.propertyOptions.length) {
          self._set({
            step:            'options',
            propertyName:    result.propertyName,
            propertyOptions: result.propertyOptions,
          });
        } else {
          // "Not found" is a different failure than "API down" — only the
          // not-found case warrants the Request Vehicle CTA below the error.
          self._set({
            step:      'error',
            errorKind: 'not-found',
            error:     "We couldn't find that vehicle in our catalog yet."
          });
        }
      })
      .catch(function (err) {
        console.error('[MMVehiclePicker]', err);
        self._set({
          step:      'error',
          errorKind: 'transient',
          error:     'Unable to reach the vehicle database. Please try again.'
        });
      });
  };

  MMVehiclePicker.prototype._confirm = function () {
    var s   = this.state;
    var m   = s.matched;
    // Dedupe: when the trim/submodel is already present in the model name
    // (e.g. model="Golf GTI" + submodel="GTI", model="Camry XSE" + submodel="XSE",
    // model="Mustang GT" + submodel="GT") the naive join produces "Golf GTI GTI".
    // Drop the submodel from the label when it's a token already in the model.
    var modelStr = m.model    || '';
    var subStr   = m.submodel || '';
    if (subStr) {
      var modelTokens = modelStr.toLowerCase().split(/\s+/);
      if (modelTokens.indexOf(subStr.toLowerCase()) > -1) {
        subStr = '';
      }
    }
    var lbl = [s.year, m.make, modelStr, subStr].filter(Boolean).join(' ');

    var vehicle = {
      muvId:            m.muvId,
      year:             s.year,
      category:         s.category,
      make:             m.make,
      model:            m.model,
      submodel:         m.submodel,
      driveType:        m.driveType,
      transmissionType: m.transmissionType,
      engineName:       m.engineName,
      displayLabel:     lbl,
    };

    MMGarage.set(vehicle);
    this.onSelect(vehicle);
  };

  MMVehiclePicker.prototype._restart = function () {
    this._resetState();
    this._render();
  };

  /* Go back one step in the selection flow */
  MMVehiclePicker.prototype._goBack = function () {
    var s = this.state;
    if (s.step === 'type') return;

    // From year → type
    if (s.step === 'year') {
      this._restart();
      return;
    }

    // From error or loading → type
    if (s.step === 'error' || s.step === 'loading') {
      this._restart();
      return;
    }

    if (s.step === 'options') {
      var propName = s.propertyName;

      if (propName === 'make') {
        // Back to year — no re-query needed
        Object.assign(this.state, {
          step: 'year',
          makeId: null, makeLabel: null,
          modelId: null, modelLabel: null,
          submodel: null, driveType: null,
          transmissionType: null, engine: null, bodyOrCabStyle: null,
          propertyName: null, propertyOptions: []
        });
        this._render();
        return;
      }

      if (propName === 'model') {
        // Back to make — re-query without makeId
        Object.assign(this.state, {
          step: 'loading',
          makeId: null, makeLabel: null,
          modelId: null, modelLabel: null,
          submodel: null, driveType: null,
          transmissionType: null, engine: null, bodyOrCabStyle: null,
          propertyName: null, propertyOptions: []
        });
        this._render();
        this._query();
        return;
      }

      // Deeper (submodel, driveType, etc.) → back to model
      Object.assign(this.state, {
        step: 'loading',
        submodel: null, driveType: null,
        transmissionType: null, engine: null, bodyOrCabStyle: null,
        propertyName: null, propertyOptions: []
      });
      this._render();
      this._query();
      return;
    }

    // Fallback
    this._restart();
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */

  MMVehiclePicker.prototype._render = function () {
    if (!this.container) return;
    var s   = this.state;
    var self = this;
    var root = this.container;
    root.innerHTML = '';
    // Add mm-picker without wiping existing classes (e.g. mm-catalog-picker-inline)
    root.classList.add('mm-picker');

    /* Breadcrumb — only shown when past the type step */
    var bc = this._renderBreadcrumb();
    if (bc) root.appendChild(bc);

    /* Step content */
    var content;
    switch (s.step) {
      case 'type':    content = this._renderTypeStep(); break;
      case 'year':    content = this._renderYearStep(); break;
      case 'loading': content = this._renderLoading();     break;
      case 'options': content = this._renderOptionsStep(); break;
      case 'matched': content = this._renderMatched();     break;
      case 'error':   content = this._renderError();       break;
    }
    if (content) root.appendChild(content);

    /* Persistent "Don't see your vehicle?" footer.
     *   - Hidden on 'type' (customer hasn't started picking yet — premature)
     *   - Hidden on 'matched' (success, no need)
     *   - Hidden on 'error' (CTA is already shown next to the error message)
     *   - Hidden on 'loading' (transient, would look like noise)
     * Shown on year + options — the steps where a customer would realize
     * mid-flow that their make/model/year isn't in the grid. */
    if (s.step === 'year' || s.step === 'options') {
      var footer = el('div', 'mm-picker__missing-footer');
      var link = document.createElement('a');
      link.href = self._buildRequestUrl('picker_mid_flow');
      link.className = 'mm-picker__missing-link';
      link.innerHTML = "Don't see your vehicle? <strong>Request it &rarr;</strong>";
      footer.appendChild(link);
      root.appendChild(footer);
    }
  };

  /* Build /pages/cant-find-your-vehicle?...&source=... with whatever the customer
   * has narrowed down so far. The request page reads these params to
   * pre-fill matching fields — the customer never repeats themselves. */
  MMVehiclePicker.prototype._buildRequestUrl = function (source) {
    var s = this.state;
    var params = new URLSearchParams();
    if (s.category)   params.set('type',  s.category);
    if (s.year)       params.set('year',  String(s.year));
    if (s.makeLabel)  params.set('make',  s.makeLabel);
    if (s.modelLabel) params.set('model', s.modelLabel);
    if (s.submodel)   params.set('trim',  s.submodel);
    if (s.engine)     params.set('engine', s.engine);
    if (s.driveType || s.transmissionType) {
      params.set('drive', s.driveType || s.transmissionType);
    }
    params.set('source', source || 'picker');
    return '/pages/cant-find-your-vehicle?' + params.toString();
  };

  MMVehiclePicker.prototype._renderBreadcrumb = function () {
    var s    = this.state;
    var self = this;
    var wrap = el('div', 'mm-picker__breadcrumb');

    // On type step there's nothing to show — return null so it's not appended at all
    if (s.step === 'type') return null;

    // Left side: ← Back button
    var back = el('button', 'mm-picker__back-btn', '← Back');
    back.addEventListener('click', function () { self._goBack(); });
    wrap.appendChild(back);

    // Center: breadcrumb trail showing selections so far
    var parts = [];
    if (s.typeLabel || s.category) parts.push(s.typeLabel || s.category);
    if (s.year)                     parts.push(s.year);
    if (s.makeLabel)                parts.push(s.makeLabel);
    if (s.modelLabel)               parts.push(s.modelLabel);

    if (parts.length) {
      var trail = el('span', 'mm-picker__crumb-trail');
      trail.innerHTML = parts.map(function (p, i) {
        var isCurrent = i === parts.length - 1;
        return '<span class="mm-picker__crumb' + (isCurrent ? ' mm-picker__crumb--current' : '') + '">'
          + p + '</span>';
      }).join('<span class="mm-picker__crumb-sep">›</span>');
      wrap.appendChild(trail);
    }

    // Right side: ✕ Cancel (restart to type)
    var cancel = el('button', 'mm-picker__cancel-btn', '✕');
    cancel.setAttribute('aria-label', 'Cancel vehicle selection');
    cancel.addEventListener('click', function () { self._restart(); });
    wrap.appendChild(cancel);

    return wrap;
  };

  MMVehiclePicker.prototype._renderTypeStep = function () {
    var self = this;
    var wrap = el('div', 'mm-picker__step mm-picker__step--type');
    var grid = el('div', 'mm-picker__type-grid');

    VEHICLE_TYPES.forEach(function (type) {
      var card = el('button', 'mm-picker__type-card',
        '<span class="mm-picker__type-icon">' + (VEHICLE_ICONS[type.value] || '') + '</span>' +
        '<span class="mm-picker__type-label">' + type.label + '</span>'
      );
      card.addEventListener('click', function () { self._pickType(type); });
      grid.appendChild(card);
    });

    wrap.appendChild(grid);
    return wrap;
  };

MMVehiclePicker.prototype._renderYearStep = function () {
    var self = this;
    var wrap  = el('div', 'mm-picker__step mm-picker__step--year');
    var title = el('h3', 'mm-picker__step-title', 'Select Year');
    wrap.appendChild(title);

    var listWrap = el('div', 'mm-picker__options-list-wrap');

    // Search input — makes scanning 50+ years fast
    var search = document.createElement('input');
    search.type        = 'text';
    search.placeholder = 'Search year…';
    search.className   = 'mm-picker__option-search';
    search.setAttribute('autocomplete', 'off');
    listWrap.appendChild(search);

    var list = el('div', 'mm-picker__options-list');

    for (var y = YEAR_MAX; y >= YEAR_MIN; y--) {
      (function (year) {
        var btn = el('button', 'mm-picker__option-item', String(year));
        btn.dataset.label = String(year);
        btn.addEventListener('click', function () { self._pickYear(year); });
        list.appendChild(btn);
      }(y));
    }

    search.addEventListener('input', function (e) {
      var q = e.target.value.toLowerCase();
      list.querySelectorAll('.mm-picker__option-item').forEach(function (item) {
        item.style.display = item.dataset.label.indexOf(q) > -1 ? '' : 'none';
      });
    });

    listWrap.appendChild(list);
    wrap.appendChild(listWrap);
    return wrap;
  };

  MMVehiclePicker.prototype._renderLoading = function () {
    return el('div', 'mm-picker__loading',
      '<div class="mm-picker__spinner"></div>' +
      '<p class="mm-picker__loading-text">Loading…</p>'
    );
  };

  MMVehiclePicker.prototype._renderOptionsStep = function () {
    var self     = this;
    var propName = this.state.propertyName;
    var options  = this.state.propertyOptions;
    var label    = PROPERTY_LABELS[propName] || propName;

    var wrap  = el('div', 'mm-picker__step mm-picker__step--options');
    var title = el('h3', 'mm-picker__step-title', 'Select ' + label);
    wrap.appendChild(title);

    // Make / model: scrollable list with optional search
    if (propName === 'make' || propName === 'model') {
      wrap.appendChild(this._renderOptionsList(options));
    } else {
      wrap.appendChild(this._renderOptionsGrid(options));
    }

    return wrap;
  };

  MMVehiclePicker.prototype._renderOptionsList = function (options) {
    var self = this;
    var wrap = el('div', 'mm-picker__options-list-wrap');

    if (options.length > 8) {
      var search = document.createElement('input');
      search.type        = 'text';
      search.placeholder = 'Search…';
      search.className   = 'mm-picker__option-search';
      search.setAttribute('autocomplete', 'off');
      search.addEventListener('input', function (e) {
        var q = e.target.value.toLowerCase();
        list.querySelectorAll('.mm-picker__option-item').forEach(function (item) {
          item.style.display = item.dataset.label.toLowerCase().indexOf(q) > -1 ? '' : 'none';
        });
      });
      wrap.appendChild(search);
    }

    var list = el('div', 'mm-picker__options-list');
    options.forEach(function (opt) {
      var btn = el('button', 'mm-picker__option-item', opt.value);
      btn.dataset.label = opt.value;
      btn.addEventListener('click', function () { self._pickOption(opt); });
      list.appendChild(btn);
    });

    wrap.appendChild(list);
    return wrap;
  };

  MMVehiclePicker.prototype._renderOptionsGrid = function (options) {
    var self = this;
    var grid = el('div', 'mm-picker__options-grid');
    options.forEach(function (opt) {
      var btn = el('button', 'mm-picker__option-btn', opt.value);
      btn.addEventListener('click', function () { self._pickOption(opt); });
      grid.appendChild(btn);
    });
    return grid;
  };


  MMVehiclePicker.prototype._renderError = function () {
    var self = this;
    var notFound = this.state.errorKind === 'not-found';
    var wrap = el('div', 'mm-picker__step mm-picker__step--error',
      '<div class="mm-picker__error-icon">' + (notFound ? '🔍' : '⚠') + '</div>' +
      '<p class="mm-picker__error-msg">' + (this.state.error || 'Something went wrong.') + '</p>'
    );
    var retry = el('button', 'btn btn--outline mm-picker__retry-btn', 'Try Again');
    retry.addEventListener('click', function () { self._restart(); });
    wrap.appendChild(retry);
    // Only surface the Request Vehicle CTA when the failure was "we don't
    // have it" — not for transient API errors where retrying would work.
    if (notFound) {
      var requestLink = document.createElement('a');
      requestLink.href = self._buildRequestUrl('picker_not_found');
      requestLink.className = 'mm-picker__request-link';
      requestLink.innerHTML = "Don't see your vehicle? <strong>Request it →</strong>";
      wrap.appendChild(requestLink);
    }
    return wrap;
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * MMVehiclePickerModal — modal wrapper around MMVehiclePicker
   * ───────────────────────────────────────────────────────────────────────── */

  function MMVehiclePickerModal() {
    this._overlay = null;
  }

  MMVehiclePickerModal.prototype.open = function (options) {
    if (this._overlay) return;
    options = options || {};

    var self    = this;
    var overlay = el('div', 'mm-picker-modal__overlay');
    var panel   = el('div', 'mm-picker-modal__panel');
    var header  = el('div', 'mm-picker-modal__header',
      '<span class="mm-picker-modal__title">Select Your Vehicle</span>'
    );
    var closeBtn = el('button', 'mm-picker-modal__close',
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
    );
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', function () { self.close(); });
    header.appendChild(closeBtn);

    var body = el('div', 'mm-picker-modal__body');

    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Select your vehicle');
    panel.appendChild(header);
    panel.appendChild(body);

    overlay.appendChild(panel);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) self.close();
    });

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      overlay.classList.add('mm-picker-modal--open');
    });

    this._overlay = overlay;

    new MMVehiclePicker({
      container:  body,
      showCancel: false,
      onSelect:   function (vehicle) {
        self.close();
        if (options.onSelect) options.onSelect(vehicle);
      },
    });
  };

  MMVehiclePickerModal.prototype.close = function () {
    if (!this._overlay) return;
    var overlay = this._overlay;
    this._overlay = null;
    overlay.classList.remove('mm-picker-modal--open');
    document.body.style.overflow = '';
    setTimeout(function () {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 300);
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * Garage chip initializer
   * ───────────────────────────────────────────────────────────────────────── */

  function initGarageChip() {
    var chip = document.getElementById('mm-garage-chip');
    if (!chip) return;

    var modal = new MMVehiclePickerModal();

    function updateChip() {
      var v = MMGarage.get();
      if (v) {
        chip.innerHTML =
          '<span class="mm-garage-chip__icon">🚗</span>' +
          '<span class="mm-garage-chip__label">' + v.displayLabel + '</span>' +
          '<button class="mm-garage-chip__change" aria-label="Change vehicle">Change</button>';
        chip.classList.add('mm-garage-chip--has-vehicle');

        chip.querySelector('.mm-garage-chip__change').addEventListener('click', function (e) {
          e.stopPropagation();
          MMGarage.clear();
        });
      } else {
        chip.innerHTML =
          '<span class="mm-garage-chip__icon">🔧</span>' +
          '<span class="mm-garage-chip__label">Select Vehicle</span>';
        chip.classList.remove('mm-garage-chip--has-vehicle');
      }
    }

    chip.addEventListener('click', function (e) {
      if (e.target.classList.contains('mm-garage-chip__change')) return;
      modal.open({
        onSelect: function () {
          updateChip();
          var onCatalog = !!document.getElementById('mm-catalog-root');
          if (!onCatalog) window.location.href = '/collections/all';
        },
      });
    });

    document.addEventListener('mm:vehicle:selected', updateChip);
    document.addEventListener('mm:vehicle:cleared',  updateChip);
    updateChip();
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Inline picker initializer
   * Used on both the shop landing hero AND catalog/search/fitment result pages.
   * ───────────────────────────────────────────────────────────────────────── */

  function initInlinePicker() {
    var root = document.getElementById('mm-picker-inline');
    if (!root) return;

    // Detect result pages — on these pages selecting a vehicle filters in-place
    // rather than navigating away, and the "Shop Parts →" CTA is hidden.
    var isResultPage = !!(
      document.getElementById('mm-catalog-root') ||
      document.getElementById('mm-search-root')  ||
      document.querySelector('.mm-fitment-page')
    );

    function spawnPicker() {
      root.innerHTML = '';
      new MMVehiclePicker({
        container:  root,
        showCancel: false,
        onSelect:   function () {
          // No navigation on select — showSelected() renders the confirmation
          // banner via the mm:vehicle:selected listener below, and the user
          // chooses when to leave the landing page by clicking "Shop Parts".
          // On result pages the same listener triggers in-place filtering.
        },
      });
    }

    function showSelected(vehicle) {
      root.innerHTML = '';
      root.classList.remove('mm-picker');

      if (isResultPage) {
        // Compact "filtering for" bar — no "Shop Parts" CTA
        var wrap = document.createElement('div');
        wrap.className = 'mm-vehicle-selected mm-vehicle-selected--catalog';
        wrap.innerHTML =
          '<div class="mm-vehicle-selected__left">' +
          '  <span class="mm-vehicle-selected__check">✓</span>' +
          '  <div class="mm-vehicle-selected__info">' +
          '    <span class="mm-vehicle-selected__label">Showing parts for</span>' +
          '    <p class="mm-vehicle-selected__name">' + vehicle.displayLabel + '</p>' +
          '  </div>' +
          '</div>' +
          '<button class="mm-vehicle-selected__change mm-change-vehicle-inline">Change</button>';
        root.appendChild(wrap);

        wrap.querySelector('.mm-change-vehicle-inline').addEventListener('click', function () {
          MMGarage.clear(); // fires mm:vehicle:cleared → spawnPicker via listener
        });

      } else {
        // Landing page: full selected-vehicle banner with the picker confirmation.
        // Three actions: "Change Vehicle" (re-pick), "Shop Parts →" (proceed),
        // and a corner "X" that clears the selection without re-opening the picker.
        var wrap2 = document.createElement('div');
        wrap2.className = 'mm-vehicle-selected mm-vehicle-selected--landing';
        wrap2.innerHTML =
          '<button class="mm-vehicle-selected__close" aria-label="Cancel vehicle selection">' +
          '  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
          '<div class="mm-vehicle-selected__left">' +
          '  <div class="mm-vehicle-selected__check">✓</div>' +
          '  <div class="mm-vehicle-selected__info">' +
          '    <span class="mm-vehicle-selected__label">Your Vehicle</span>' +
          '    <p class="mm-vehicle-selected__name">' + vehicle.displayLabel + '</p>' +
          '  </div>' +
          '</div>' +
          '<div class="mm-vehicle-selected__actions">' +
          '  <button class="mm-vehicle-selected__change mm-change-vehicle-inline">Change Vehicle</button>' +
          '  <a href="/collections/all" class="btn btn--primary mm-vehicle-selected__cta">Show Parts →</a>' +
          '</div>';
        root.appendChild(wrap2);

        wrap2.querySelector('.mm-change-vehicle-inline').addEventListener('click', function () {
          MMGarage.clear(); // fires mm:vehicle:cleared → spawnPicker via listener
        });
        wrap2.querySelector('.mm-vehicle-selected__close').addEventListener('click', function () {
          MMGarage.clear();
        });
      }
    }

    // Event listeners — always set up regardless of initial state
    document.addEventListener('mm:vehicle:selected', function (e) {
      showSelected(e.detail.vehicle);
    });
    document.addEventListener('mm:vehicle:cleared', function () {
      spawnPicker();
    });

    // Initial render
    var stored = MMGarage.get();
    if (stored) {
      showSelected(stored);
    } else {
      spawnPicker();
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * Bootstrap
   * ───────────────────────────────────────────────────────────────────────── */

  function init() {
    initGarageChip();
    initInlinePicker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose globals
  window.MMGarage             = MMGarage;
  window.MMVehiclePicker      = MMVehiclePicker;
  window.MMVehiclePickerModal = MMVehiclePickerModal;

}(window));
